import { useContext } from "react";
import { BASE_API, useHttp } from "../hooks/http";
import { AppStoreContext } from "../provider/AppStoreProvider";
import { Concern, Language } from "../models/enums";


export interface IUser {
    id: number;
    email?: string;
    full_name: string;
    data?: {
        language: Language
        concerns: Concern[]
    };
}

export class User implements IUser {
    public id: number;
    public email?: string;
    public full_name: string;
    public data?: IUser["data"];

    constructor(user: IUser) {
        this.id = user.id;
        this.email = user.email;
        this.full_name = user.full_name;
        this.data = user.data;
    }

    get shortName() {
        return this.full_name.split(" ")[0];
    }
}

export type UpdateUserRequest = Pick<IUser, "full_name">;

export const useMe = () => {
    const http = useHttp(BASE_API, true);

    const appContext = useContext(AppStoreContext);

    const fetchMyProfile = async (): Promise<void> => {
        await http.get<IUser>('/patientapi/me/')
            .then((response) => {
                if (response?.data) {
                    appContext.myProfile?.set(new User(response.data));
                }
            });
    };

    const updateMyProfile = async (data: UpdateUserRequest): Promise<void> => {
        await http.put<IUser>('/patientapi/me/', data)
            .then((response) => {
                if (!response?.ok) {
                    throw new Error("Failed to update profile");
                }

                const value = appContext.myProfile?.value;
                if (value) {
                    value.full_name = data.full_name;
                    appContext.myProfile?.set(value);
                }
            })
    };

    const cancelWithMyTherapist = async (): Promise<void> => {
        await http.delete('/patientapi/me/therapist')
            .then((response) => {
                if (response?.ok) {
                    appContext.myTherapist?.set(undefined);
                }
            });
    };

    return {
        fetchMyProfile,
        updateMyProfile,
        cancelWithMyTherapist
    };
};
